import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import queryString from "query-string";

import { useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { appConstants } from "../../constants";
import { ApiErrorsContext } from "../../contexts";

import { logDataToConsole, transformQueryParams } from "../../utils";
import { useAuth } from "../useAuth";
import { useNotifications } from "../useNotifications";
import { useGetWebHostEnvironment } from "../useGetWebHostEnvironment";
import { useAccountSettings } from "../useAccountSettings";

export interface ISwitchappAxiosClientConfig<TRequestData>
  extends AxiosRequestConfig<TRequestData> {
  hideApiErrorMessge?: boolean;
  hideResponseMessage?: boolean;
  hideAxiosErrorMessages?: boolean;
  ignoreActivityBlocker?: boolean;
  parseHttpResponse?: (
    httpResponse: AxiosResponse<any, any>
  ) => AxiosResponse<any, any>;
}

// interface IHasApiErrors {
//   errors: any;
// }

export function useSwitchappAxiosClient<TRequestData>(
  switchAppAxiosConfig: ISwitchappAxiosClientConfig<TRequestData>,
  tenantId?: string
): AxiosInstance {
  const switchappAxiosClient: AxiosInstance = axios.create({
    ...switchAppAxiosConfig,
    paramsSerializer: (params) => {
      const transformedParams = transformQueryParams(params);
      return new URLSearchParams(transformedParams).toString();
    },
  });

  const { data: accountSettings, refreshRemoteData: refreshAccountSettings } =
    useAccountSettings();

  const isLive = accountSettings?.is_live || false;

  const hostingEnv = useGetWebHostEnvironment();
  const requestTargetEnv = useGetWebHostEnvironment(
    switchAppAxiosConfig.baseURL
  );
  const isRequestGoingToWrongEnv = hostingEnv !== requestTargetEnv;

  const authContext = useAuth();
  const notificationsHandler = useNotifications();

  const location = useLocation();
  const { apiErrors, setApiErrors, clearApiErrors } =
    useContext(ApiErrorsContext);

  useEffect(() => {
    clearApiErrors();
  }, [location]);

  function displayApiError(errorTitle: string, errorMessage: string) {
    logDataToConsole(errorMessage, errorTitle);

    if (!switchAppAxiosConfig?.hideApiErrorMessge)
      notificationsHandler.showNotification({
        ariaLabel: "error notification",
        title: errorTitle,
        kind: "error",
        message: errorMessage,
        statusIconDescription: "error",
        timeout: 10000,
      });

    if (!apiErrors) alert(errorMessage);
  }

  // this interceptor is called before every request
  switchappAxiosClient.interceptors.request.use((reqConfig) => {
    const token = authContext.token;
    const api_key = authContext.api_key;
    const session_id = authContext.session_id;
    const checkout_session_id = authContext.checkout_session_id;

    // configure serialization of object query params
    // reqConfig.paramsSerializer = (params) => {
    //   // Qs is not included in the Axios package
    //   return queryString.stringifyUrl(params, {
    //     arrayFormat: "bracket",
    //     encode: false,
    //   });
    // };

    // confirm request is going to correct env
    const WRONG_ENV_ERROR_MESSAGE = `ERROR: The request is originating from ${hostingEnv} env but being sent to ${requestTargetEnv} env`;
    if (isRequestGoingToWrongEnv) {
      alert(WRONG_ENV_ERROR_MESSAGE);
      throw new Error(WRONG_ENV_ERROR_MESSAGE);
    }

    // clear api errors if any
    clearApiErrors();

    // handle refresh token

    // add token to the request if available (only some auth requests do not need a token)
    if (token && reqConfig.headers)
      reqConfig.headers.Authorization = "Bearer " + token;

    // add api_key to the request if available
    if (api_key && reqConfig.headers) {
      // req.headers[appConstants.api_keyHeaderName] = api_key; // in x-api-key format
      reqConfig.headers.Authorization = "Bearer " + api_key; // in Bearer format
    }

    // add isTestMode
    if (!isLive && reqConfig.headers) {
      reqConfig.headers[appConstants.isTestModeHeaderName] = "Test"; // in x-api-mode format
    }

    // add tenant id to the request if available
    if (tenantId && reqConfig.headers) {
      reqConfig.headers[appConstants.tenantHeaderName] = tenantId;
    }

    // add dashboard session id to the request if available
    if (session_id && reqConfig.headers) {
      reqConfig.headers[appConstants.sessionIdHeaderName] = session_id;
    }

    // add checkout session id to the request if available
    if (checkout_session_id && reqConfig.headers) {
      reqConfig.headers[appConstants.checkoutSessionIdHeaderName] =
        checkout_session_id;
    }

    // log application api requests to reporting service here
    logDataToConsole(reqConfig, "Api Request [Axios Interceptor]: ");

    return reqConfig;
  });

  // this interceptor is called after every response
  switchappAxiosClient.interceptors.response.use(
    (resp: any) => {
      // log application api responses to reporting service here
      logDataToConsole(resp, "Api Response [Axios Interceptor]: ");

      return !switchAppAxiosConfig?.parseHttpResponse
        ? resp
        : switchAppAxiosConfig.parseHttpResponse(resp);
    },

    (error: AxiosError</*responseType*/ any, TRequestData>) => {
      const { response = { status: null } } = error; // .toJSON();
      const { status = null } = response;

      // get validation errors if available
      const apiErrors: any = error?.response?.data?.errors;
      if (apiErrors) {
        setApiErrors(apiErrors);
      }

      // log application api errors to reporting service here
      logDataToConsole(error.response, "[Axios Interceptor error] response: ");

      // handle error messages and statuses
      if (error.code === "ERR_NETWORK") {
        displayApiError(
          "Network error",
          "No Network Connection. Please Check Your Internet Connection."
        );
      } else if (error.code === "ECONNABORTED") {
        // cancelled requests
        logDataToConsole("ECONNABORTED", "ECONNABORTED");

        // display error message
        displayApiError("Request Cancelled", "Request cancelled by user");
      } else if (status && status === 401) {
        displayApiError(
          "You Are Not Signed In",
          "Please sign in to your account."
        );

        // clear api key; TODO: display overlay for login if route is not authRoute
        authContext.setToken(null);
        authContext.signOut(); // redirect user to login again;
      } else if (status && status === 403) {
        displayApiError(
          "Access denied",
          "You do not have permission to access this resource."
        );
      } else if (status && status === 404) {
        displayApiError(
          "Resource not found",
          "The requested resource was not found."
        );
      } else if (status && status === 500) {
        displayApiError(
          "Error occured",
          // "An internal error has occurred.",
          "Oops... Something seems to be broken. Totally our fault. Please retry later."
        );
      } else if (status && status === 503) {
        displayApiError(
          "Service unavailable",
          "The service is unavailable. Please try again later."
        );
      } else if (status && status === 504) {
        displayApiError(
          "Gateway timeout",
          "The gateway timed out. Please try again later."
        );
      } else {
        if (error.response && !switchAppAxiosConfig?.hideApiErrorMessge) {
          const { data = {} } = error.response;
          const errorMessage = data["error"] || data["msg"] || data["message"];

          if (errorMessage) {
            displayApiError("Message", errorMessage);
          } else {
            displayApiError(
              "Error",
              "An error has occurred. Please review your request."
            );
          }
        }
      }

      // const jsonError = error.toJSON();
      // return Promise.reject(jsonError);

      return Promise.reject(error.response as any);
      // return Promise.reject(error.response as unknown as ISwAppServiceApi<any>);
    }
  );

  return switchappAxiosClient;
}
